import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  AssetLoader,
  countriesRequiringConsents,
  getCookie,
  Geo,
  Logger,
  LoggerTransformer,
  QueryString,
  reportError,
  TopLevelDomainExtractor,
  waitFor,
} from "@identity/utils";
import { NAVIGATOR, WINDOW } from "@identity/symbols";
import { ConsentResolvedCallback } from "../../types";
import {
  ConsentProviderInterface,
  ConsentStubProviderInterface,
} from "../consent-provider-interface";
import {
  CommunicationService,
  eventsRepository,
} from "../../../CommunicationService";
import { OneTrustTracker } from "./one-trust-tracker";
import { USP_VALUES, UsPrivacy } from "./us-privacy";
import {
  checkOneTrustModalClosed,
  checkOneTrustOptIn,
  getDefaultOneTrustCategories,
  getOneTrustConsentCategories,
  getOptanonConsent,
  hasOptanonGroups,
  OptanonConsent,
} from "./one-trust-utils";

const ONE_TRUST_DOMAIN_IDS = {
  ".fanatical.com": "0ab0e172-dc9c-44b9-8a58-c80d9ba4fae0",
  ".futhead.com": "82c642a4-d3a9-4a78-a310-21a7b49ab17b",
  ".gamespot.com": "73588546-b116-4dbc-ab64-9db97e11fc0d",
  ".metacritic.com": "50e16f1d-a929-4cc0-80a7-3c5c303eea6a",
  ".muthead.com": "fb34a16e-696d-4748-af9f-96f3b9e2b4da",
  ".tvguide.com": "ffc22311-f4da-4e03-a1ad-32aecd522c5b",
  ".fandom.com": "dea70a1b-c82d-4fe0-86ff-5e164b0a6022",
  ".fandom-dev.pl": "0195d37b-9d1b-7a59-ae6e-31a365aca256",
  ".fandom-dev.us": "0195d387-ba18-754d-9ad7-8673011966c3",
} as const;

@singleton()
export class OneTrust
  implements ConsentProviderInterface, ConsentStubProviderInterface
{
  private readonly testMode = !!QueryString.get("ot_test");
  private enableDelayedActionEvent = false;
  private initialized = false;
  private readonly UI_READINESS_AWAIT_TIME = 100;
  private readonly UI_READINESS_TRIES = 100;

  constructor(
    @inject(WINDOW) private readonly window: Window,
    @inject(NAVIGATOR) private readonly navigator: Navigator,
    @inject(AssetLoader) private readonly assetLoader: AssetLoader,
    @inject(Geo) private readonly geo: Geo,
    @inject(UsPrivacy) private readonly usPrivacy: UsPrivacy,
    @injectWithTransform(Logger, LoggerTransformer, "OneTrust")
    private readonly logger: Logger,
    @inject(OneTrustTracker) private oneTrustTracker: OneTrustTracker,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
  ) {
    this.oneTrustTracker.init();
    this.loadOneTrustScripts();
    this.waitForOneTrustSDK()
      .then(() => {
        this.initializeListeners();
      })
      .catch((ex) => {
        void reportError(ex);
      });
    this.addChangeListener();
  }

  getConsentApi(): "tcf" | "usp" | undefined {
    return this.geo.getCountryCode() === "us"
      ? "usp"
      : this.geo.isGdprCountry()
        ? "tcf"
        : undefined;
  }

  getConsentString(): string | undefined {
    return this.getConsentApi() === "tcf"
      ? (getCookie("eupubconsent-v2") ?? undefined)
      : this.getConsentApi() === "usp"
        ? this.usPrivacy.getPrivacyString()
        : undefined;
  }

  getConsents(optOut: boolean): Required<Categories> | undefined {
    const optanonConsent = this.getOneTrustConsentCookie();
    return hasOptanonGroups(optanonConsent)
      ? getOneTrustConsentCategories(optanonConsent)
      : getDefaultOneTrustCategories(!optOut, { necessary: true });
  }

  subscribe(callback: ConsentResolvedCallback) {
    const modalClosed = checkOneTrustModalClosed();
    if (modalClosed || !this.geo.isGdprCountry()) {
      this.consentsUpdated(callback);
    }
    this.initialize(callback);
  }

  private oneTrustScriptLoaded(): boolean {
    const script = document.querySelectorAll(
      `script[data-domain-script="${this.getDomainId()}"]`,
    );
    let oneTrustLoaded = false;
    script.forEach((s) => {
      if (s.getAttribute("src")?.includes("otSDKStub.js")) {
        oneTrustLoaded = true;
      }
    });
    return oneTrustLoaded;
  }

  private initialize(callback: ConsentResolvedCallback): void {
    this.logger.info("Initializing OneTrust");
    const oldOptanonWrapper = this.window.OptanonWrapper;
    this.window.OptanonWrapper = () => {
      this.logger.info("OneTrust Wrapper Called");
      const modalClosed = checkOneTrustModalClosed();
      const lawRestriction =
        this.geo.isGdprCountry() || this.geo.getCountryCode() === "us";
      if (modalClosed || !lawRestriction) {
        this.consentsUpdated(callback);
        return oldOptanonWrapper?.();
      }
    };
  }

  private getOneTrustConsentCookie(): OptanonConsent | undefined {
    const cookie = getCookie("OptanonConsent");
    if (!cookie) {
      return undefined;
    }
    return getOptanonConsent(cookie);
  }

  private readTrackingConsents(): boolean {
    const isOptOut = checkOneTrustOptIn();

    if (isOptOut === undefined) {
      return (
        this.geo.getCountryCode() === "us" &&
        !this.navigator.globalPrivacyControl
      );
    }

    return isOptOut;
  }

  private consentsUpdated(callback: ConsentResolvedCallback): void {
    if (this.geo.isGdprCountry() && !this.getOneTrustConsentCookie()) {
      this.logger.info("OneTrust cookie not found");
      return;
    }

    const location = this.geo.getCountryCode();
    const trackingConsent = this.readTrackingConsents();

    this.initialized = true;
    if (location) {
      // In GDPR user gives us consent. In CCPA user gives us a signal to OptOut.
      // Therefore, in US we assume, that signal = true, means that user opted-out.
      if (location === "us") {
        if (this.navigator.globalPrivacyControl) {
          this.logger.info("OneTrust GPC registered");
          this.oneTrustTracker.sendGPCEvent();
          // OneTrust takes its time to update the cookie,
          // so let's do it ourselves faster
          this.usPrivacy.createPrivacyCookie(USP_VALUES.yes);
          this.handleDelayedEvent(false);
          callback({
            gdprConsent: true,
            geoRequiresConsent: false,
            geoRequiresSignal: true,
            ccpaSignal: true,
          });
          return;
        }
        // OneTrust takes its time to update the cookie,
        // so let's do it ourselves faster
        this.usPrivacy.createPrivacyCookie(
          trackingConsent ? USP_VALUES.no : USP_VALUES.yes,
        );
      }
      this.handleDelayedEvent(trackingConsent);

      callback({
        gdprConsent: countriesRequiringConsents.includes(location)
          ? trackingConsent
          : true,
        geoRequiresConsent: countriesRequiringConsents.includes(location),
        geoRequiresSignal: location === "us",
        ccpaSignal: location === "us" ? !trackingConsent : false,
      });
    }
  }

  private async loadOneTrustScripts() {
    this.logger.info("Loading OneTrust scripts");
    if (!this.oneTrustScriptLoaded()) {
      this.logger.info("Loading OneTrust SDK");
      await this.assetLoader.loadScript(
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
        "text/javascript",
        true,
        null,
        {
          charSet: "UTF-8",
          "data-domain-script": this.getDomainId(),
        },
      );
    }

    const countryCode = this.geo.getCountryCode() ?? "";
    if (
      countriesRequiringConsents.includes(countryCode) &&
      this.window.__tcfapi === undefined
    ) {
      this.logger.info("Loading OneTrust TCF stub");
      await this.assetLoader.loadScript(
        "https://cdn.cookielaw.org/consent/tcf.stub.js",
        "text/javascript",
        true,
        null,
        {
          charSet: "UTF-8",
        },
      );
    }

    if (countryCode === "us" && this.window.__uspapi === undefined) {
      this.logger.info("Loading OneTrust USPAPI");
      await this.assetLoader.loadScript(
        "https://cdn.cookielaw.org/opt-out/otCCPAiab.js",
        "text/javascript",
        true,
        null,
        {
          charSet: "UTF-8",
          "ccpa-opt-out-geo": "us",
          "ccpa-opt-out-ids": "C0004",
          "ccpa-opt-out-lspa": "false",
        },
      );
    }
  }

  private getDomainId() {
    const tld = TopLevelDomainExtractor.extract(this.window.location.hostname);
    const domainId =
      ONE_TRUST_DOMAIN_IDS[tld] ?? ONE_TRUST_DOMAIN_IDS[".fandom.com"];

    return this.testMode ? `${domainId}-test` : domainId;
  }

  private addChangeListener(): void {
    this.communicationService.on(
      eventsRepository.UPDATE_CONSENT,
      ({ optOut }: { optOut: boolean }) => {
        this.setConsent(optOut);
      },
    );
  }

  async setConsent(optOut: boolean): Promise<void> {
    const groupValue = optOut ? "0" : "1";
    this.logger.info("Setting OneTrust consent", groupValue);
    await waitFor(() => !!this.window.OneTrust?.UpdateConsent, 50);

    this.window.OneTrust!.UpdateConsent(
      "Category",
      `C0001:1,C0002:1,C0004:${groupValue},C0003:1,C0005:1`,
    );
  }

  private async initializeListeners() {
    this.logger.info("Initializing listeners");
    this.addClickListener();
    this.addImpressionListener();
  }

  private async waitForOneTrustSDK(): Promise<void> {
    let tries = 0;
    try {
      await waitFor(
        () => {
          tries++;
          this.logger.info(`Waiting for SDK (${tries})`);
          return (
            typeof document !== "undefined" &&
            !!document.querySelector("#onetrust-consent-sdk")
          );
        },
        this.UI_READINESS_TRIES,
        this.UI_READINESS_AWAIT_TIME,
        10,
      );
    } catch (e) {
      throw new Error("OneTrust SDK was not initialized");
    }
  }

  private addClickListener(): void {
    document
      .querySelector("#onetrust-consent-sdk")
      ?.addEventListener("click", (event: Event) => {
        switch ((event.target as HTMLDivElement).id) {
          case "onetrust-accept-btn-handler":
            return this.oneTrustTracker.sendActionEvent("accept");
          case "accept-recommended-btn-handler":
            return this.oneTrustTracker.sendActionEvent("accept");
          case "onetrust-reject-btn-handler":
            return this.oneTrustTracker.sendActionEvent("reject");
          case "onetrust-reject-all-handler":
            return this.oneTrustTracker.sendActionEvent("reject");
          default: {
            const eventTargetClasses = (event.target as HTMLDivElement)
              .classList;

            if (eventTargetClasses.contains("ot-pc-refuse-all-handler")) {
              return this.oneTrustTracker.sendActionEvent("reject");
            } else if (eventTargetClasses.contains("onetrust-close-btn-ui")) {
              return this.oneTrustTracker.sendActionEvent("close");
            } else if (
              eventTargetClasses.contains("save-preference-btn-handler")
            ) {
              if (this.initialized) {
                this.logger.info("OneTrust modal action is being sent");
                setTimeout(() => {
                  this.oneTrustTracker.sendActionEvent(
                    this.readTrackingConsents() ? "accept" : "reject",
                  );
                });
              } else {
                this.logger.info("OneTrust modal is not visible");
                this.enableDelayedActionEvent = true;
              }
            }
          }
        }
      });
  }

  private addImpressionListener(): void {
    const config = {
      attributes: false,
      childList: false,
      characterData: true,
    };
    document
      .querySelector(".ot-sdk-show-settings")
      ?.addEventListener("click", () => {
        this.oneTrustTracker.sendImpressionEvent();
      });

    const targetNode = document.getElementById("onetrust-consent-sdk");
    if (document.getElementById("onetrust-banner-sdk")) {
      this.oneTrustTracker.sendImpressionEvent();
    }

    if (targetNode) {
      const observer = new MutationObserver((event) => {
        this.logger.info("Checking if modal is shown ", event);
        if (document.querySelector(".ot-sdk-container")) {
          this.logger.info("OneTrust modal is visible");
          this.oneTrustTracker.sendImpressionEvent();
        } else {
          this.logger.info("OneTrust modal is not visible");
        }
      });
      observer.observe(targetNode, config);
    }
  }

  private handleDelayedEvent(trackingAllowed: boolean): void {
    this.logger.info("Handling delayed action event ", trackingAllowed);
    if (this.enableDelayedActionEvent) {
      this.enableDelayedActionEvent = false;
      this.oneTrustTracker.sendActionEvent(
        trackingAllowed ? "accept" : "reject",
      );
    }
  }
}
